/**
 * LessonCitationsPanel — student-facing sources block for a lesson.
 *
 * Fetches `/api/lms/lessons/{id}/citations` and renders a compact panel
 * of the KB documents the lesson references. Pairs with the transform
 * helper `transformCiteMarkers()` which is used to swap inline
 * `[cite:doc-id]` markers in lesson prose with numbered `[N]` labels
 * matching the panel below.
 *
 * Also exposes the "Listen" (audiobook) toggle button next to the
 * panel header, keeping all lesson-level enrichments in one place.
 */
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { BookOpen, Headphones, X } from 'lucide-react';

// Exported so LessonViewer can preprocess prose fields before rendering.
// citations must be the array returned by the backend, ORDER PRESERVED.
export const transformCiteMarkers = (raw, citations) => {
  if (!raw || !citations || citations.length === 0) return raw;
  const idxById = new Map(citations.map((c, i) => [c.doc_id, i + 1]));
  return raw.replace(/\[cite:([a-zA-Z0-9\-]+)\]/g, (_, id) => {
    const n = idxById.get(id);
    return n ? `[${n}]` : '';
  });
};

// Map a citation's `source_kind` + `doc_id` to a deep-link URL.
// Documents / text-entries have no viewer route so we fall back to the
// snippet modal for those.
const deepLinkForCitation = (cit) => {
  if (!cit) return null;
  if (cit.source_kind === 'lesson' && cit.doc_id) return `/lesson/${cit.doc_id}`;
  if (cit.source_kind === 'forum_thread' && cit.doc_id) return `/community/t/${cit.doc_id}`;
  return null;
};

const kindLabel = (kind) => {
  if (kind === 'document') return 'Document';
  if (kind === 'lesson') return 'Lesson';
  if (kind === 'forum_thread') return 'Forum thread';
  if (kind === 'text_entry') return 'Knowledge entry';
  return kind || 'Source';
};

const SourceModal = ({ cit, onClose }) => {
  if (!cit) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur"
      onClick={onClose}
      data-testid="lesson-source-modal"
    >
      <div
        className="bg-background border border-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Source · {cit.source_kind === 'document' ? 'Uploaded document' : 'Knowledge entry'}
            </div>
            <h3 className="text-lg font-bold mt-1">{cit.source_title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-foreground" data-testid="lesson-source-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
            {cit.snippet || 'No preview snippet available for this source.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function LessonCitationsPanel({ lessonId, citations: injected }) {
  const { API } = useContext(AuthContext);
  const [citations, setCitations] = useState(injected || []);
  const [loading, setLoading] = useState(!injected);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (injected) { setCitations(injected); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lms/lessons/${lessonId}/citations`);
        if (!cancelled) setCitations(data || []);
      } catch { /* soft-fail — panel just doesn't render */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [API, lessonId, injected]);

  if (loading) return null;

  // Panel only surfaces when we actually have citations — the audiobook
  // launcher moved to the Lesson Tools sidebar card above so we don't
  // double-render it here.
  if (citations.length === 0) return null;
  const hasCitations = true;

  return (
    <>
      <Card className="mb-4" data-testid="lesson-citations-panel">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen className="w-4 h-4 text-primary" />
            Cited sources ({citations.length})
          </CardTitle>
        </CardHeader>
        {hasCitations ? (
          <CardContent className="pt-0">
            <p className="text-[11px] text-slate-500 mb-2">
              This lesson is grounded in the following knowledge library documents.
              Numbers `[1] [2]` in the text link to these sources — click any card
              to preview.
            </p>
            <div className="grid gap-2 sm:grid-cols-2" data-testid="lesson-citations-grid">
              {citations.map((c, i) => {
                const link = deepLinkForCitation(c);
                const handleClick = () => {
                  if (link) window.open(link, '_blank', 'noopener,noreferrer');
                  else setActive(c);
                };
                return (
                  <button
                    key={c.doc_id + i}
                    type="button"
                    onClick={handleClick}
                    className="text-left rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 hover:border-primary hover:bg-primary/5 transition-colors p-3"
                    data-testid={`lesson-source-card-${i + 1}`}
                    title={link ? `Open ${kindLabel(c.source_kind)} in a new tab` : 'View source snippet'}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary text-primary-foreground text-[10px] font-bold shrink-0">
                        {i + 1}
                      </span>
                      <span className="font-semibold text-sm truncate">{c.source_title}</span>
                    </div>
                    {c.snippet ? (
                      <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-2">{c.snippet}</p>
                    ) : null}
                    <div className="mt-1 text-[10px] text-slate-500 uppercase tracking-wide">
                      {kindLabel(c.source_kind)} · {link ? 'Open source ↗' : 'View source'}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        ) : null}
      </Card>

      <SourceModal cit={active} onClose={() => setActive(null)} />
    </>
  );
}
